import { Component, inject, signal, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { NgbOffcanvasModule, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HelpArticleService } from '../../services/help-article.service';
import { HelpDocumentService } from '../../services/help-document.service';
import { renderMarkdown } from '../../utils/markdown.util';
import { PermissionService } from '@services/core/permission.service';

@Component({
  selector: 'app-help-panel',
  standalone: true,
  imports: [CommonModule, NgbOffcanvasModule, RouterLink],
  templateUrl: './help-panel.component.html',
  styleUrl: './help-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class HelpPanelComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private helpService = inject(HelpArticleService);
  private docService = inject(HelpDocumentService);
  private offcanvas = inject(NgbOffcanvas);
  private sanitizer = inject(DomSanitizer);
  readonly perm = inject(PermissionService);

  currentPath = signal('');
  article = signal<any | null>(null);
  loading = signal(false);
  notFound = signal(false);
  bodyHtml = signal<SafeHtml>('');

  private navSub?: Subscription;

  ngOnInit(): void {
    this.refreshPath(this.router.url);
    this.navSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.refreshPath(e.urlAfterRedirects || e.url));
  }

  ngOnDestroy(): void {
    this.navSub?.unsubscribe();
  }

  private refreshPath(url: string): void {
    this.currentPath.set(HelpArticleService.normalizeRoutePath(url));
    this.article.set(null);
    this.notFound.set(false);
  }

  open(content: any): void {
    this.loading.set(true);
    this.notFound.set(false);
    this.article.set(null);
    this.helpService.getByRoute(this.currentPath()).subscribe({
      next: (row) => {
        this.article.set(row);
        this.bodyHtml.set(
          this.sanitizer.bypassSecurityTrustHtml(renderMarkdown(row?.bodyMd))
        );
        this.loading.set(false);
        this.offcanvas.open(content, { position: 'end', panelClass: 'help-offcanvas' });
      },
      error: () => {
        this.notFound.set(true);
        this.loading.set(false);
        this.offcanvas.open(content, { position: 'end', panelClass: 'help-offcanvas' });
      }
    });
  }

  viewUrl(row: any): string | null {
    const uuid = row?.document?.uuid || row?.uuid;
    return uuid ? this.docService.viewUrl(uuid) : null;
  }

  canManageHelp(): boolean {
    return this.perm.can('configuracion/ayuda', 'canRead')
      || this.perm.can('configuracion/ayuda', 'canCreate')
      || this.perm.can('configuracion/ayuda', 'canUpdate');
  }
}
